import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginPage } from '../../identity/pages/LoginPage';
import { useIdentity } from '../../identity/state/useIdentity';
import type { LoginResponse } from '../../identity/types';
import { isSafeReturnTo } from '../../../shared/isSafeReturnTo';
import { PARTNER_LOGIN_SLIDES } from '../heroSlides';
import { evaluatePartnerEligibility } from '../partnerEligibility';
import { PartnerAccountInactivePage } from './PartnerAccountInactivePage';

/**
 * EPN-36 — Partner Sign In. Reuses LoginPage's entire credential/lockout/MFA/remember-me engine unmodified
 * (see IAuthService/AuthEndpoints on the backend — nothing here calls a different login endpoint, issues a
 * different cookie, or re-implements any of that); this component only supplies different hero imagery/
 * footer links (via LoginPage's own optional props) and one new thing LoginPage's default behavior doesn't
 * have: a post-login eligibility check.
 * <p>
 * `/partner-login` is itself one of App.tsx's PUBLIC_ROUTE_PREFIXES entries (it has to be — unlike plain
 * `/login`, nothing gates it behind an authenticated-session check), which means App.tsx's public-route
 * branch keeps rendering this page's own `<Routes>` block UNCONDITIONALLY even after `me` becomes
 * populated — there is no implicit "fall through to the authenticated tree" the way plain `/login` gets.
 * So every branch below navigates explicitly, honoring `?returnTo=` exactly like LoginPage's own default
 * behavior does.
 * <p>
 * There is no separate Partner dashboard anywhere in this app yet (see
 * docs/epn-36-partner-signin-gaps.md) — an eligible Partner account is routed to "/" , landing in the
 * exact same Admin Portal any other signed-in user reaches. A valid credential with NO Partner-channel
 * membership at all is treated as "wrong front door, not a security event" and also proceeds there — the
 * alternative (a hard block) would punish a legitimate Admin user who simply bookmarked this page, for a
 * credential that was never actually compromised. Only a genuine Partner credential whose company has been
 * deactivated is actually blocked, since that is the one case this ticket's own account-status requirement
 * is meant to catch — see `evaluatePartnerEligibility`'s own doc comment for the full policy.
 */
export function PartnerLoginPage() {
  const { logout } = useIdentity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [blocked, setBlocked] = useState(false);

  if (blocked) {
    return <PartnerAccountInactivePage />;
  }

  async function handleLoginSuccess(result: LoginResponse) {
    const eligibility = evaluatePartnerEligibility(result.memberships);
    if (eligibility === 'inactive') {
      // The credential check already issued real session cookies (AuthEndpoints' /login handler has no
      // concept of "which page the caller came from") — revoke them now, rather than leaving the block
      // purely cosmetic (see PartnerAccountInactivePage's own doc comment). Awaited, with its failure
      // contained here: the account genuinely is a real, inactive Partner credential either way, so the
      // blocked message stays accurate even if the revocation call itself fails — but a silent
      // fire-and-forget call would risk the opposite of this branch's whole purpose, an unrevoked session
      // sitting behind a UI that claims to have blocked it.
      try {
        await logout();
      } catch {
        // Revocation failed server-side; the credential is still a real, inactive Partner account, so the
        // blocked message below remains accurate regardless — nothing else to do with this failure here.
      }
      setBlocked(true);
      return;
    }
    const returnTo = searchParams.get('returnTo');
    navigate(isSafeReturnTo(returnTo) ? returnTo : '/', { replace: true });
  }

  return (
    <LoginPage
      heroSlides={PARTNER_LOGIN_SLIDES}
      signInHref="/partner-login"
      forgotPasswordHref="/partner-forgot-password"
      signUpHref="/signup/partner"
      showPartnerSignUpLink={false}
      onLoginSuccess={handleLoginSuccess}
    />
  );
}
